"use client";

import { useEffect, useCallback, useRef } from "react";
import { usePathname } from "next/navigation";
import { useAppStore } from "@/store/appStore";
import { clientLogger } from "@/lib/logger";
import { getCookie, deleteCookie } from "cookies-next/client";

/**
 * Global component that detects when the server has flagged auth as expired.
 *
 * When the server-side axios interceptor gets a 401, it:
 *   1. Deletes the Authorization cookie
 *   2. Sets a short-lived "auth-expired" flag cookie (client-readable)
 *
 * This component detects the "auth-expired" cookie and triggers a full
 * page reload to re-sync server and client state.
 *
 * Place this once in the root layout.
 */
export default function UnauthorizedHandler() {
	const pathname = usePathname();
	const isHandling = useRef(false);

	const handleUnauthorized = useCallback(() => {
		if (isHandling.current) return;
		isHandling.current = true;

		clientLogger.warn("UnauthorizedHandler: Auth expired detected, reloading");

		// Remove the flag cookie so we don't loop on reload
		deleteCookie("auth-expired", { path: "/" });

		// Clear persisted auth state before reload so rehydration doesn't skip guest login
		const { setData, setCookielessData } = useAppStore.getState();
		setData({ authorization: "" });
		setCookielessData({ authorization: "", user: null, isUserReady: true });
		// Also delete the Authorization cookie from client side in case it wasn't already
		deleteCookie("Authorization", { path: "/" });

		// Hard reload to fully re-sync server and client state
		window.location.reload();
	}, []);

	// Check if the server has set the "auth-expired" flag cookie
	const checkAuthExpired = useCallback(() => {
		if (isHandling.current) return;

		const expiredFlag = getCookie("auth-expired");

		if (expiredFlag) {
			clientLogger.warn("UnauthorizedHandler: Found auth-expired flag cookie");
			handleUnauthorized();
		}
	}, [handleUnauthorized]);

	// Check on route changes
	useEffect(() => {
		checkAuthExpired();
	}, [pathname, checkAuthExpired]);

	// Check on user clicks, tab focus, and periodically
	useEffect(() => {
		const handleClick = () => checkAuthExpired();
		const handleVisibility = () => {
			if (document.visibilityState === "visible") {
				checkAuthExpired();
			}
		};

		document.addEventListener("click", handleClick, true);
		document.addEventListener("visibilitychange", handleVisibility);

		// Also poll every 3 seconds as a safety net — the flag cookie is
		// set by the server response, so it can appear at any time after
		// a server action completes.
		const interval = setInterval(checkAuthExpired, 3000);

		return () => {
			document.removeEventListener("click", handleClick, true);
			document.removeEventListener("visibilitychange", handleVisibility);
			clearInterval(interval);
		};
	}, [checkAuthExpired]);

	return null;
}
